import { LEVELS, TIERS } from '@xox/shared'
import { Play } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui'
import { LevelCard, TierHeader } from './LevelCard'
import { isUnlocked, loadProgress, type Progress, summarizeProgress } from './progress'

export function LevelSelect() {
  const [progress, setProgress] = useState<Progress>({})
  const [open, setOpen] = useState<Record<number, boolean>>({})
  useEffect(() => {
    setProgress(loadProgress())
  }, [])

  const { cleared, next: current, nextNumber } = summarizeProgress(progress, LEVELS)
  const currentTier = current?.tier ?? 1
  const isOpen = (tierId: number) => open[tierId] ?? tierId === currentTier

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
        <div>
          <div className="text-sm font-semibold">
            {cleared} / {LEVELS.length} levels cleared
          </div>
          <div className="h-1.5 w-48 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${(cleared / LEVELS.length) * 100}%` }}
            />
          </div>
        </div>
        {current && (
          <Link to={`/ice/play/${current.id}`}>
            <Button>
              <Play className="size-4" aria-hidden /> Continue · #{nextNumber} {current.name}
            </Button>
          </Link>
        )}
      </div>

      {TIERS.map((tier) => {
        const levels = LEVELS.slice(tier.from - 1, tier.to)
        const done = levels.filter((l) => progress[l.id]).length
        const unlockedAny = levels.some((l) => isUnlocked(progress, LEVELS, l.id))
        const expanded = isOpen(tier.id)
        return (
          <section key={tier.id} aria-labelledby={`tier-${tier.id}`}>
            <TierHeader
              tier={tier}
              expanded={expanded}
              onToggle={() => setOpen((o) => ({ ...o, [tier.id]: !expanded }))}
              locked={!unlockedAny}
              meta={`${done}/${levels.length}`}
            />
            {expanded && (
              <ul className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                {levels.map((level) => (
                  <LevelCard
                    key={level.id}
                    level={level}
                    number={LEVELS.indexOf(level) + 1}
                    unlocked={isUnlocked(progress, LEVELS, level.id)}
                    done={progress[level.id]}
                  />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
